import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { alpha, useTheme } from '@mui/material/styles';
import {
  DataGridPremium,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
  type GridCellParams,
  type GridColDef,
  type GridRenderEditCellParams,
  type GridRowId,
} from '@mui/x-data-grid-premium';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  appendQuickEntryUserToUrl,
  clearStoredQuickEntryUser,
  getStoredQuickEntryUser,
  quickEntryUserMatches,
} from '../../utils/quickEntryUser';

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

type EChartsOption = echarts.EChartsCoreOption;

type LandingRecord = {
  id: string;
  sourceType: 'Patient' | 'Caregiver';
  name: string;
  mobile: string;
  phone: string;
  status: string;
  notes?: string;
  leadStage?: string;
  createdBy?: string;
  createdDate?: string;
  lastModifiedDate?: string;
  medicaidPayer?: string;
  medicaidPlanCode?: string;
  medcheckMedicaidStatus?: string;
  lineOfBusiness?: string;
  startOfCareDate?: string;
  hardToReach?: boolean;
  type?: string;
  county?: string;
  email?: string;
  primaryLanguage?: string;
};

type GridLandingRow = LandingRecord & {
  uiId: string;
};

type EditableDisplayParams = {
  id: GridRowId;
  field: string;
  value?: unknown;
  colDef: {
    headerName?: string;
  };
};

type ActivityRecord = {
  id: string;
  type: 'task' | 'event';
  subject: string;
  status?: string;
  activityDate?: string;
  startDateTime?: string;
  endDateTime?: string;
  createdDate?: string;
  description?: string;
  ownerName?: string;
};

type ActivityState = {
  isLoading: boolean;
  error: string;
  activities: ActivityRecord[];
};

type StatusChartDatum = {
  status: string;
  label: string;
  value: number;
};

type StatusBarChartProps = {
  data: StatusChartDatum[];
  selectedStatus: string | null;
  emptyDataLabel: string;
  recordsLabel: string;
  onStatusClick: (status: string) => void;
};

const formatCount = (value: number) => value.toLocaleString();
const EASTERN_TIME_ZONE = 'America/New_York';
const EXPANDED_NOTE_ROW_HEIGHT = 164;
const QUICK_ENTRY_PAGE_SIZE = 50;

const easternDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return char;
    }
  });

const buildBarGradient = (topColor: string, bottomColor: string) => ({
  type: 'linear',
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
    { offset: 0, color: topColor },
    { offset: 1, color: bottomColor },
  ],
});

function StatusBarChart({ data, selectedStatus, emptyDataLabel, recordsLabel, onStatusClick }: StatusBarChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<echarts.ECharts | null>(null);
  const theme = useTheme();

  const option = useMemo<EChartsOption>(
    () => ({
      animationDuration: 900,
      animationEasing: 'elasticOut',
      tooltip: {
        trigger: 'item',
        formatter: (params: unknown) => {
          const datum = data[(params as any)?.dataIndex];
          if (!datum) return '';
          return `<strong>${escapeHtml(datum.label)}</strong><br/>${escapeHtml(recordsLabel)}: <strong>${formatCount(datum.value)}</strong>`;
        },
      },
      grid: { left: 40, right: 18, top: 18, bottom: data.length > 4 ? 66 : 38 },
      xAxis: {
        type: 'category',
        data: data.map((item) => item.label),
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          rotate: data.length > 4 ? 35 : 0,
          color: theme.palette.text.secondary,
          fontSize: 12,
        },
        axisLine: { lineStyle: { color: theme.palette.divider } },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: { color: theme.palette.text.secondary, formatter: (value: number) => formatCount(value) },
        splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
      },
      series: [
        {
          name: recordsLabel,
          type: 'bar',
          barCategoryGap: '18%',
          barMaxWidth: 240,
          data: data.map((item) => {
            const isSelected = selectedStatus === item.status;
            const color = theme.palette.primary.main;
            const lightColor = theme.palette.primary.light;
            return {
              value: item.value,
              itemStyle: {
                color: buildBarGradient(alpha(lightColor, 0.95), color),
                borderRadius: [4, 4, 0, 0],
                opacity: selectedStatus !== null && !isSelected ? 0.45 : 1,
              },
            };
          }),
          label: {
            show: true,
            position: 'insideBottom',
            formatter: (params: any) => (params.value ? formatCount(Number(params.value)) : ''),
            color: '#ffffff',
            textBorderColor: 'rgba(0,0,0,0.65)',
            textBorderWidth: 2,
            fontWeight: 700,
            padding: [0, 0, 4, 0],
          },
        },
      ],
    }),
    [data, recordsLabel, selectedStatus, theme],
  );

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current, undefined, { renderer: 'canvas' });
    instanceRef.current = chart;

    let resizeFrame = 0;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        chart.resize();
      });
    });
    resizeObserver.observe(chartRef.current);

    return () => {
      cancelAnimationFrame(resizeFrame);
      resizeObserver.disconnect();
      chart.dispose();
      instanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    instanceRef.current?.setOption(option, true);
  }, [option]);

  useEffect(() => {
    const chart = instanceRef.current;
    if (!chart) return;

    const handleClick = (params: any) => {
      const datum = data[params.dataIndex];
      if (datum) onStatusClick(datum.status);
    };

    chart.on('click', handleClick);
    return () => {
      chart.off('click', handleClick);
    };
  }, [data, onStatusClick]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: 260 }}>
      <Box
        ref={chartRef}
        sx={{ width: '100%', height: '100%', cursor: data.length > 0 ? 'pointer' : 'default' }}
      />
      {data.length === 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ position: 'absolute', inset: 0 }}
        >
          <Typography color="text.secondary">{emptyDataLabel}</Typography>
        </Box>
      )}
    </Box>
  );
}

const formatCreatedDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

const formatDateOnly = (value?: string) => {
  if (!value) return '';
  if (value.slice(0, 10) === '9999-01-01') return '';
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${Number(month)}/${Number(day)}/${year}`;
  }
  return formatCreatedDate(value);
};

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return easternDateTimeFormatter.format(date);
};

const getActivityTimestamp = (activity: ActivityRecord) =>
  formatDateTime(activity.activityDate) ||
  formatDateTime(activity.startDateTime) ||
  formatDateTime(activity.createdDate) ||
  'N/A';

function SaveEditButton({
  rowId,
  field,
  isSaving,
  isMultiline = false,
  onSave,
}: {
  rowId: GridRowId;
  field: string;
  isSaving: boolean;
  isMultiline?: boolean;
  onSave: (rowId: GridRowId, field: string) => void | Promise<void>;
}) {
  return (
    <IconButton
      aria-label="Save"
      size="small"
      disabled={isSaving}
      onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => {
        event.stopPropagation();
        void onSave(rowId, field);
      }}
      sx={{ p: 0.5, alignSelf: isMultiline ? 'flex-start' : 'stretch', mt: isMultiline ? 1 : 0 }}
    >
      {isSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
    </IconButton>
  );
}

function NotesEditCell({
  params,
  isSaving,
  onSave,
}: {
  params: GridRenderEditCellParams<GridLandingRow, string>;
  isSaving: boolean;
  onSave: (rowId: GridRowId, field: string) => void;
}) {
  const [value, setValue] = useState(String(params.value || ''));
  const didPlaceCursorRef = useRef(false);

  return (
    <Box display="flex" alignItems="flex-start" gap={0.75} width="100%" height="100%">
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={4}
        maxRows={5}
        value={value}
        variant="outlined"
        inputRef={(input) => {
          if (!input || didPlaceCursorRef.current) return;
          didPlaceCursorRef.current = true;
          window.requestAnimationFrame(() => {
            const end = value.length;
            input.setSelectionRange(end, end);
          });
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          setValue(nextValue);
          void params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: nextValue,
          });
        }}
        sx={{
          py: 1,
          '& .MuiInputBase-root': {
            alignItems: 'flex-start',
            fontSize: '0.875rem',
            lineHeight: 1.4,
            p: 1,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      />
      <SaveEditButton
        rowId={params.id}
        field={params.field}
        isSaving={isSaving}
        isMultiline
        onSave={async () => {
          const didApplyValue = await params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value,
          });
          if (didApplyValue === false) return;
          await onSave(params.id, params.field);
        }}
      />
    </Box>
  );
}

export default function QuickEntryLanding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiRef = useGridApiRef();
  const { authFetch, user } = useAuth();
  const [quickEntryUser, setQuickEntryUser] = useState(() => getStoredQuickEntryUser());
  const canClearQuickEntryUserFilter = !searchParams.has('user') && !searchParams.has('USER');
  const qeId = user?.username?.startsWith('qe_') ? user.username.slice(3) : null;
  const newYorkPortalUrlBase = qeId
    ? `https://portal.linkhomecare.com/start?qe=true&id=${encodeURIComponent(qeId)}`
    : 'https://portal.linkhomecare.com/start?qe=true';
  const newYorkPortalUrl = appendQuickEntryUserToUrl(newYorkPortalUrlBase, quickEntryUser);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);
  const [patientRecords, setPatientRecords] = useState<LandingRecord[]>([]);
  const [caregiverRecords, setCaregiverRecords] = useState<LandingRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(new Set());
  const [expandedNoteRowIds, setExpandedNoteRowIds] = useState<Set<GridRowId>>(new Set());
  const [savingCellKey, setSavingCellKey] = useState('');
  const [activitiesByRecordId, setActivitiesByRecordId] = useState<Record<string, ActivityState>>({});
  const activitiesByRecordIdRef = useRef<Record<string, ActivityState>>({});

  const exportFileName = `${activeTab === 0 ? 'Patients' : 'Caregivers'}-${new Date().toISOString().slice(0, 10)}`;
  const exportOptions = useMemo(
    () => ({ fileName: exportFileName, utf8WithBom: true }),
    [exportFileName],
  );

  useEffect(() => {
    activitiesByRecordIdRef.current = activitiesByRecordId;
  }, [activitiesByRecordId]);

  const expandNoteRow = (rowId: GridRowId) => {
    setExpandedNoteRowIds((prev) => {
      if (prev.has(rowId)) return prev;
      const next = new Set(prev);
      next.add(rowId);
      return next;
    });
    window.requestAnimationFrame(() => {
      apiRef.current?.resetRowHeights();
    });
  };

  const toggleNoteRow = (rowId: GridRowId) => {
    setExpandedNoteRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
    window.requestAnimationFrame(() => {
      apiRef.current?.resetRowHeights();
    });
  };

  const startCellEdit = (rowId: GridRowId, field: string) => {
    window.requestAnimationFrame(() => {
      try {
        apiRef.current?.startCellEditMode({ id: rowId, field });
      } catch {
        // The grid can throw if the cell is already in edit mode.
      }
    });
  };

  const startEditableCell = (rowId: GridRowId, field: string) => {
    if (field === 'notes') {
      expandNoteRow(rowId);
    }
    startCellEdit(rowId, field);
  };

  const stopEditableCell = (rowId: GridRowId, field: string) => {
    try {
      apiRef.current?.stopCellEditMode({ id: rowId, field });
    } catch {
      // The grid can throw if edit mode has already been stopped.
    }
  };

  const saveEditedCell = (rowId: GridRowId, field: string) => {
    setSavingCellKey(`${rowId}-${field}`);
    window.requestAnimationFrame(() => stopEditableCell(rowId, field));
  };

  const renderEditableDisplayCell = (params: EditableDisplayParams, isExpanded = false) => (
    <Box
      display="flex"
      alignItems={isExpanded ? 'flex-start' : 'center'}
      gap={0.75}
      width="100%"
      height="100%"
    >
      <Typography
        variant="body2"
        title={String(params.value || '')}
        sx={{
          flex: 1,
          py: isExpanded ? 1 : 0,
          whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
          overflowWrap: isExpanded ? 'anywhere' : 'normal',
          overflowX: 'hidden',
          overflowY: isExpanded ? 'auto' : 'hidden',
          textOverflow: isExpanded ? 'clip' : 'ellipsis',
          lineHeight: 1.4,
          maxHeight: '100%',
          minWidth: 0,
          pr: isExpanded ? 0.5 : 0,
        }}
      >
        {String(params.value || '')}
      </Typography>
      <IconButton
        aria-label={`Edit ${params.colDef.headerName || params.field}`}
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          startEditableCell(params.id, params.field);
        }}
        sx={{ p: 0.25, alignSelf: isExpanded ? 'flex-start' : 'center', mt: isExpanded ? 0.75 : 0 }}
      >
        <EditIcon fontSize="inherit" />
      </IconButton>
    </Box>
  );

  useEffect(() => {
    const loadRecords = async () => {
      setIsLoading(true);
      setError('');
      try {
        const res = await authFetch('/api/quick-entry');
        if (!res.ok) throw new Error(t('qe.failedToLoadLeads'));
        const data = await res.json();
        setPatientRecords(data.records || []);
        setCaregiverRecords(data.caregiverRecords || []);
      } catch (err: any) {
        setError(err?.message || t('qe.unableToLoad'));
      } finally {
        setIsLoading(false);
      }
    };
    void loadRecords();
  }, []);

  const patientColumns = useMemo<GridColDef[]>(
    () => [
      { field: 'name', headerName: t('qe.grid.name'), flex: 1.4, minWidth: 220 },
      { field: 'mobile', headerName: t('qe.grid.mobile'), flex: 1, minWidth: 140 },
      { field: 'phone', headerName: t('qe.grid.phone'), flex: 1, minWidth: 140 },
      { field: 'status', headerName: t('qe.grid.status'), flex: 1, minWidth: 160 },
      { field: 'lineOfBusiness', headerName: t('qe.grid.lineOfBusiness'), flex: 1, minWidth: 180 },
      { field: 'startOfCareDate', headerName: t('qe.grid.startOfCareDate'), flex: 1, minWidth: 170 },
      {
        field: 'hardToReach',
        headerName: t('qe.grid.hardToReach'),
        flex: 0.8,
        minWidth: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <Checkbox checked={!!params.value} disabled size="small" />,
      },
      { field: 'createdBy', headerName: t('qe.grid.createdBy'), flex: 1, minWidth: 130 },
      { field: 'createdDate', headerName: t('qe.grid.createdDate'), flex: 1, minWidth: 150 },
    ],
    [t],
  );

  const caregiverColumns = useMemo<GridColDef[]>(
    () => [
      { field: 'name', headerName: t('qe.grid.name'), flex: 1.4, minWidth: 220 },
      { field: 'type', headerName: t('qe.grid.type'), flex: 0.7, minWidth: 100 },
      { field: 'county', headerName: t('qe.grid.county'), flex: 0.9, minWidth: 130 },
      { field: 'mobile', headerName: t('qe.grid.mobile'), flex: 1, minWidth: 140 },
      { field: 'phone', headerName: t('qe.grid.phone'), flex: 1, minWidth: 140 },
      { field: 'email', headerName: t('qe.grid.email'), flex: 1.2, minWidth: 200 },
      { field: 'status', headerName: t('qe.grid.status'), flex: 1, minWidth: 160 },
      { field: 'primaryLanguage', headerName: t('qe.grid.primaryLanguage'), flex: 1, minWidth: 160 },
      { field: 'createdBy', headerName: t('qe.grid.createdBy'), flex: 1, minWidth: 130 },
      { field: 'createdDate', headerName: t('qe.grid.createdDate'), flex: 1, minWidth: 150 },
    ],
    [t],
  );

  const notesColumn = useMemo<GridColDef>(
    () => ({
      field: 'notes',
      headerName: t('qe.grid.notes'),
      flex: 1.4,
      minWidth: 260,
      editable: true,
      renderEditCell: (params) => (
        <NotesEditCell
          params={params}
          isSaving={savingCellKey === `${params.id}-${params.field}`}
          onSave={(rowId, field) => saveEditedCell(rowId, field)}
        />
      ),
      renderCell: (params) => {
        const isExpanded = expandedNoteRowIds.has(params.id);
        return renderEditableDisplayCell(params, isExpanded);
      },
    }),
    [expandedNoteRowIds, savingCellKey, t],
  );

  const patientGridRows = useMemo<GridLandingRow[]>(
    () =>
      patientRecords.map((record, index) => ({
        ...record,
        uiId: record.id || `Patient-${index}`,
        name: record.name || 'N/A',
        mobile: record.mobile || '',
        phone: record.phone || '',
        status: record.status?.toLowerCase() === 'lead' && record.leadStage
          ? record.leadStage
          : record.status || '',
        lineOfBusiness: record.lineOfBusiness || '',
        startOfCareDate: formatDateOnly(record.startOfCareDate),
        hardToReach: record.hardToReach ?? false,
        notes: record.notes || '',
        createdBy: record.createdBy || '',
        createdDate: formatCreatedDate(record.createdDate),
      })),
    [patientRecords],
  );

  const caregiverGridRows = useMemo<GridLandingRow[]>(
    () =>
      caregiverRecords.map((record, index) => ({
        ...record,
        uiId: record.id || `Caregiver-${index}`,
        name: record.name || 'N/A',
        type: record.type || '',
        county: record.county || '',
        mobile: record.mobile || '',
        phone: record.phone || '',
        email: record.email || '',
        status: record.status?.toLowerCase() === 'lead' && record.leadStage
          ? record.leadStage
          : record.status || '',
        primaryLanguage: record.primaryLanguage || '',
        notes: record.notes || '',
        createdBy: record.createdBy || '',
        createdDate: formatCreatedDate(record.createdDate),
      })),
    [caregiverRecords],
  );

  const activeColumns = activeTab === 0 ? patientColumns : caregiverColumns;
  const activeColumnsWithNotes = useMemo(
    () => [...activeColumns.slice(0, -1), notesColumn, ...activeColumns.slice(-1)],
    [activeColumns, notesColumn],
  );
  const activeGridRows = activeTab === 0 ? patientGridRows : caregiverGridRows;
  const quickEntryUserGridRows = useMemo(
    () => activeGridRows.filter((row) => quickEntryUserMatches(row.createdBy, quickEntryUser)),
    [activeGridRows, quickEntryUser],
  );
  const noStatusLabel = t('qe.chart.noStatus');
  const statusChartData = useMemo<StatusChartDatum[]>(() => {
    const counts = new Map<string, number>();

    quickEntryUserGridRows.forEach((row) => {
      const status = row.status?.trim() || '';
      counts.set(status, (counts.get(status) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([status, value]) => ({
        status,
        value,
        label: status || noStatusLabel,
      }))
      .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  }, [quickEntryUserGridRows, noStatusLabel]);
  const selectedStatusLabel =
    selectedStatusFilter === null ? '' : selectedStatusFilter || noStatusLabel;
  const filteredGridRows = useMemo(
    () =>
      selectedStatusFilter === null
        ? quickEntryUserGridRows
        : quickEntryUserGridRows.filter((row) => (row.status?.trim() || '') === selectedStatusFilter),
    [quickEntryUserGridRows, selectedStatusFilter],
  );
  const handleStatusChartClick = (status: string) => {
    setSelectedStatusFilter((previous) => (previous === status ? null : status));
    setExpandedRowIds(new Set());
  };
  const handleClearStatusFilter = () => {
    setSelectedStatusFilter(null);
    setExpandedRowIds(new Set());
  };

  const handleClearQuickEntryUserFilter = () => {
    clearStoredQuickEntryUser();
    setQuickEntryUser('');
    setSelectedStatusFilter(null);
    setExpandedRowIds(new Set());
  };

  const fetchActivitiesForRecord = async (record: GridLandingRow) => {
    const rowKey = record.uiId;
    const recordId = record.id;
    if (!recordId) return;

    const existing = activitiesByRecordIdRef.current[rowKey];
    if (existing?.isLoading || existing?.activities?.length || existing?.error) return;

    setActivitiesByRecordId((prev) => ({
      ...prev,
      [rowKey]: { isLoading: true, error: '', activities: [] },
    }));

    try {
      const res = await authFetch(`/api/quick-entry/${record.sourceType}/${recordId}/activities`);
      if (!res.ok) throw new Error('Unable to load activities.');
      const data = await res.json();

      setActivitiesByRecordId((prev) => ({
        ...prev,
        [rowKey]: { isLoading: false, error: '', activities: data.activities || [] },
      }));
    } catch (err: any) {
      setActivitiesByRecordId((prev) => ({
        ...prev,
        [rowKey]: {
          isLoading: false,
          error: err?.message || 'Unable to load activities.',
          activities: [],
        },
      }));
    }
  };

  const handleExpandForRow = (row: GridLandingRow) => {
    if (!row?.uiId) return;

    let isExpanding = false;
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.uiId)) {
        next.delete(row.uiId);
      } else {
        next.add(row.uiId);
        isExpanding = true;
      }
      return next;
    });

    if (isExpanding) {
      void fetchActivitiesForRecord(row);
    }
  };

  const handleCellClick = (params: GridCellParams, event?: any) => {
    if (params.field === '__detail_panel_toggle__') return;
    if (params.field === 'notes') {
      event?.stopPropagation?.();
      const cellMode = apiRef.current?.getCellMode(params.id, 'notes');
      if (cellMode === 'edit') return;
      toggleNoteRow(params.id);
      return;
    }
    handleExpandForRow(params.row as GridLandingRow);
  };

  const handleCellDoubleClick = (params: GridCellParams) => {
    if (params.field !== 'notes') return;
    expandNoteRow(params.id);
  };

  const handleProcessRowUpdate = async (updatedRow: GridLandingRow, originalRow: GridLandingRow) => {
    const nextNotes = String(updatedRow.notes || '').trim();
    const previousNotes = String(originalRow.notes || '').trim();
    if (nextNotes === previousNotes) {
      setSavingCellKey('');
      return originalRow;
    }

    let data: any;
    try {
      setSavingCellKey(`${updatedRow.uiId}-notes`);
      const res = await authFetch(`/api/quick-entry/${updatedRow.sourceType}/${updatedRow.id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ medallionNotes: nextNotes }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || t('qe.unableToUpdateNotes'));
      }

      data = await res.json();
    } finally {
      setSavingCellKey('');
    }

    const nextRow = { ...updatedRow, notes: data.notes || '' };
    const updateRecord = (record: LandingRecord) =>
      record.id === nextRow.id && record.sourceType === nextRow.sourceType
        ? { ...record, notes: nextRow.notes }
        : record;

    if (updatedRow.sourceType === 'Patient') {
      setPatientRecords((prev) => prev.map(updateRecord));
    } else {
      setCaregiverRecords((prev) => prev.map(updateRecord));
    }

    return nextRow;
  };

  const handleProcessRowUpdateError = (err: unknown) => {
    setError(err instanceof Error ? err.message : t('qe.unableToUpdateNotes'));
  };

  const handleExpandedRowIdsChange = (ids: GridRowId[] | Set<GridRowId>) => {
    const nextIds = ids instanceof Set ? ids : new Set(ids);
    const newlyExpandedIds = Array.from(nextIds).filter((id) => !expandedRowIds.has(id));
    setExpandedRowIds(new Set(nextIds));
    newlyExpandedIds.forEach((id) => {
      const row = filteredGridRows.find((item) => item.uiId === id);
      if (row) void fetchActivitiesForRecord(row);
    });
  };

  const handleOpenNewYorkPortal = () => {
    window.open(newYorkPortalUrl, '_blank', 'noopener,noreferrer');
  };

  const handleNewLead = () => {
    navigate(appendQuickEntryUserToUrl('/quick-entry', quickEntryUser));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedStatusFilter(null);
    setExpandedRowIds(new Set());
    setExpandedNoteRowIds(new Set());
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 6, px: { xs: 2, md: 4, lg: 6 } }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="h5">{t('qe.title')}</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={handleOpenNewYorkPortal}>
              {t('qe.switchToNewYork')}
            </Button>
            <Button variant="contained" sx={{ textTransform: 'none' }} onClick={handleNewLead}>
              {t('qe.newLead')}
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('qe.referredByOrg')}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('qe.tabPatients')} sx={{ textTransform: 'none' }} />
          <Tab label={t('qe.tabCaregivers')} sx={{ textTransform: 'none' }} />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t(activeTab === 0 ? 'qe.chart.patientStatus' : 'qe.chart.caregiverStatus')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('qe.chart.subtitle')}
                  </Typography>
                </Box>
                {selectedStatusFilter !== null && (
                  <Chip
                    label={t('qe.chart.filteredBy', { status: selectedStatusLabel })}
                    onDelete={handleClearStatusFilter}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {quickEntryUser && (
                  <Chip
                    label={t('qe.filteredByUser', { user: quickEntryUser })}
                    onDelete={canClearQuickEntryUserFilter ? handleClearQuickEntryUserFilter : undefined}
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
              <StatusBarChart
                data={statusChartData}
                selectedStatus={selectedStatusFilter}
                emptyDataLabel={t('qe.chart.noData')}
                recordsLabel={t('qe.chart.records')}
                onStatusClick={handleStatusChartClick}
              />
            </Paper>

            <Box sx={{ height: 520, width: '100%' }}>
              <DataGridPremium
                key={activeTab}
                apiRef={apiRef}
                rows={filteredGridRows}
                getRowId={(row) => (row as GridLandingRow).uiId}
                columns={activeColumnsWithNotes}
                getRowHeight={(params) => (expandedNoteRowIds.has(params.id) ? EXPANDED_NOTE_ROW_HEIGHT : null)}
                sx={{
                  backgroundColor: '#fafafa',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#702B9D',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#702B9D',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: '#ffffff',
                    fontWeight: 700,
                  },
                  '& .MuiDataGrid-cell': {
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    color: 'rgba(0, 0, 0, 0.87)',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f5f0ff',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    backgroundColor: '#ffffff',
                  },
                }}
                showToolbar
                density="compact"
                pagination
                disableVirtualization
                disableColumnMenu
                disableColumnSelector
                disableRowSelectionOnClick
                onCellClick={handleCellClick}
                onCellDoubleClick={handleCellDoubleClick}
                onCellEditStart={(params) => {
                  if (params.field === 'notes') {
                    expandNoteRow(params.id);
                  }
                }}
                processRowUpdate={handleProcessRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                detailPanelExpandedRowIds={expandedRowIds}
                onDetailPanelExpandedRowIdsChange={handleExpandedRowIdsChange}
                getDetailPanelHeight={() => 'auto'}
                getDetailPanelContent={({ row }) => {
                  const rowData = row as GridLandingRow;
                  const state = activitiesByRecordId[rowData.uiId];
                  const hasLoaded = Boolean(state && !state.isLoading && !state.error);

                  return (
                    <Box sx={{ px: 3, py: 2, backgroundColor: 'grey.50' }}>
                      {!state || state.isLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">
                            {t('qe.loadingActivities')}
                          </Typography>
                        </Box>
                      ) : state?.error ? (
                        <Typography variant="body2" color="error">
                          {state.error}
                        </Typography>
                      ) : hasLoaded && state.activities.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t(rowData.sourceType === 'Patient' ? 'qe.noActivitiesPatient' : 'qe.noActivitiesCaregiver')}
                        </Typography>
                      ) : (
                        <Stack spacing={1.25}>
                          {(state?.activities || []).map((activity, index, activities) => (
                            <Box key={activity.id}>
                              <Typography variant="body2" fontWeight={600}>
                                {activity.subject || '(No subject)'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                  {getActivityTimestamp(activity)}
                                </Box>
                                {activity.status ? ` • ${activity.status}` : ''}
                                {activity.ownerName ? ` • ${activity.ownerName}` : ''}
                              </Typography>
                              {activity.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {activity.description}
                                </Typography>
                              )}
                              {index < activities.length - 1 ? <Divider sx={{ mt: 1 }} /> : null}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                }}
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: QUICK_ENTRY_PAGE_SIZE },
                  },
                }}
                slots={{
                  toolbar: () => (
                    <GridToolbarContainer
                      sx={{
                        px: 1.5,
                        py: 1,
                        justifyContent: 'flex-end',
                        gap: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                        backgroundColor: 'grey.50',
                      }}
                    >
                      <GridToolbarFilterButton />
                      <GridToolbarExport csvOptions={exportOptions} excelOptions={exportOptions} />
                    </GridToolbarContainer>
                  ),
                  noRowsOverlay: () => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                      }}
                    >
                      <Typography color="text.secondary">{t('qe.noRecords')}</Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}
